import React, { useRef } from 'react'
import iconPackRegistry from './iconPacks/registry'

interface LogoPreviewProps {
  logo: any
  onSave: () => void
  savedLogos: any[]
}

const LogoPreview = ({ logo, onSave, savedLogos }: LogoPreviewProps) => {
  const canvasRef = useRef(null) // Reference to the canvas for PNG download

  // Get the current icon pack and icon
  const currentPack = iconPackRegistry[logo?.iconPack || 'font-awesome-solid']
  const currentIcon =
    currentPack?.icons.find((i) => i.name === logo?.icon) ||
    currentPack.icons[0]

  const isExportable = typeof currentPack.exportSVG === 'function'

  // Check if current logo is already saved
  const isCurrentLogoSaved = savedLogos.some(
    (savedLogo) =>
      savedLogo.backgroundColor === logo.backgroundColor &&
      savedLogo.icon === logo.icon &&
      savedLogo.iconPack === logo.iconPack &&
      savedLogo.iconColor === logo.iconColor &&
      savedLogo.exactIconSize === logo.exactIconSize &&
      savedLogo.borderRadius === logo.borderRadius &&
      savedLogo.borderWidth === logo.borderWidth &&
      savedLogo.rotate === logo.rotate
  )

  // Handle PNG Download
  const downloadPNG = async () => {
    if (typeof currentPack.exportPNG !== 'function') {
      alert('PNG export is not supported for this icon pack.')
      return
    }
    const pngDataUrl = await currentPack.exportPNG({ icon: currentIcon, logo })
    if (!pngDataUrl) {
      alert('PNG export failed for this icon.')
      return
    }
    const link = document.createElement('a')
    link.download = 'logo.png'
    link.href = pngDataUrl
    link.click()
  }

  // Handle SVG Download
  const downloadSVG = () => {
    if (!isExportable) {
      alert('SVG export is not supported for this icon pack.')
      return
    }
    const svgContent = currentPack.exportSVG({ icon: currentIcon, logo })
    if (!svgContent) {
      alert('SVG export failed for this icon.')
      return
    }
    const blob = new Blob([svgContent], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    // Trigger download
    const link = document.createElement('a')
    link.href = url
    link.download = 'logo.svg'
    link.click()
    URL.revokeObjectURL(url)
  }

  // Handle Save
  const handleSave = () => {
    onSave()
  }

  return (
    <div className="flex flex-col items-center">
      {/* Logo Preview */}
      <div
        className="relative flex justify-center items-center bg-white border shadow-lg mx-auto"
        style={{
          width: 'min(256px, 100%)',
          height: 'min(256px, 100vw)',
          borderRadius: `${logo.borderRadius || 0}px`,
          border: `${logo.borderWidth || 0}px solid ${
            logo.iconColor || '#000000'
          }`,
          backgroundColor: logo.backgroundColor || '#ffffff',
        }}
      >
        {currentPack.getIconComponent(
          currentIcon,
          logo?.exactIconSize || 64,
          logo?.iconColor || '#000000',
          logo?.rotate || 0
        )}

        {/* Hidden Canvas for PNG download */}
        <canvas
          ref={canvasRef}
          width="256"
          height="256"
          style={{ display: 'none' }}
        />
      </div>

      {/* Warning for non-exportable packs */}
      {!isExportable && (
        <div className="text-warning text-center my-2">
          Export is not supported for this icon pack.
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4">
        <button
          className="btn btn-primary w-full sm:w-auto"
          onClick={downloadPNG}
          disabled={!isExportable}
        >
          Download PNG
        </button>
        <button
          className="btn btn-secondary w-full sm:w-auto"
          onClick={downloadSVG}
          disabled={!isExportable}
        >
          Download SVG
        </button>
        <button
          className={`btn w-full sm:w-auto ${
            isCurrentLogoSaved
              ? 'btn-success cursor-default'
              : 'btn-accent hover:btn-accent-focus'
          }`}
          onClick={handleSave}
          disabled={isCurrentLogoSaved}
          title={
            isCurrentLogoSaved
              ? 'Already saved'
              : 'Save to local browser storage'
          }
        >
          {isCurrentLogoSaved ? (
            <>
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Saved
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h-2v5.586l-1.293-1.293z" />
              </svg>
              Save
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default LogoPreview
